/**
 * "Разметить через ИИ" — cold-start a new point's crop dataset with Claude Vision.
 *
 * A new point has no annotated data, so instead of an operator labelling thousands of
 * crops by hand we pay Claude to label them once and the operator only reviews. That
 * costs real money per crop, so the button never fires blind: it opens a pre-flight
 * (crops available per table, plus any past runs) and only then offers to launch.
 */
import { useState } from 'react';
import useSWR from 'swr';
import { Loader2, Sparkles, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useT } from '@/contexts/I18nContext';
import {
  getBootstrapPrecheck,
  getBootstrapRuns,
  launchBootstrap,
  type BootstrapRun,
} from '@/api/datasets';

function RunLine({ run, t }: { run: BootstrapRun; t: (k: string) => string }) {
  const when = new Date(run.created_at).toLocaleString();
  const label =
    run.status === 'done'
      ? t('data.bootstrap.runDone').replace('{n}', String(run.labelled))
      : run.status === 'running'
        ? t('data.bootstrap.runRunning')
        : t('data.bootstrap.runFailed');
  return (
    <li className="flex items-center gap-2">
      <span className="text-muted-foreground">{when}</span>
      <span className={run.status === 'failed' ? 'text-destructive' : ''}>{label}</span>
    </li>
  );
}

export default function BootstrapButton({ zoneId }: { zoneId: number }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [launched, setLaunched] = useState(false);

  // Only fetched once the dialog is open — the precheck is cheap but pointless otherwise.
  const { data: pre, isLoading } = useSWR(
    open ? ['bootstrap-precheck', zoneId] : null,
    () => getBootstrapPrecheck(zoneId).then(r => r.data),
  );
  const { data: runs, mutate: reloadRuns } = useSWR(
    open ? ['bootstrap-runs', zoneId] : null,
    () => getBootstrapRuns(zoneId).then(r => r.data),
  );

  const onLaunch = async () => {
    setLaunching(true);
    setError(null);
    try {
      await launchBootstrap(zoneId);
      setLaunched(true);
      await reloadRuns();
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || t('data.bootstrap.launchFailed'));
    } finally {
      setLaunching(false);
    }
  };

  const close = () => {
    setOpen(false);
    setLaunched(false);
    setError(null);
  };

  const thin = pre?.tables.filter(x => !x.enough) ?? [];

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={e => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <Sparkles className="mr-1.5 h-3.5 w-3.5" />
        {t('data.bootstrap.button')}
      </Button>

      {open && (
        <Dialog open onOpenChange={o => !o && close()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('data.bootstrap.title')}</DialogTitle>
              <DialogDescription>{t('data.bootstrap.description')}</DialogDescription>
            </DialogHeader>

            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('common.loading')}
              </div>
            )}

            {pre && !launched && (
              <div className="space-y-3 text-sm">
                {!pre.onboarded && (
                  <div className="flex items-start gap-2 text-destructive">
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{t('data.bootstrap.notOnboarded')}</span>
                  </div>
                )}

                {pre.warning && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{pre.warning}</span>
                  </div>
                )}

                <div>
                  <div className="mb-1 font-medium">
                    {t('data.bootstrap.tables').replace('{n}', String(pre.tables.length))}
                  </div>
                  <ul className="max-h-40 space-y-0.5 overflow-y-auto text-xs">
                    {pre.tables.map(tb => (
                      <li key={tb.table_id} className="flex items-center justify-between gap-3">
                        <span className="truncate">{tb.name}</span>
                        <span className={tb.enough ? 'text-muted-foreground' : 'text-amber-700'}>
                          {tb.available_crops}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {thin.length > 0 && (
                    <div className="mt-1 text-xs text-amber-700">
                      {t('data.bootstrap.thin')
                        .replace('{n}', String(thin.length))
                        .replace('{target}', String(pre.target_per_table))}
                    </div>
                  )}
                </div>

                {runs && runs.length > 0 && (
                  <div>
                    <div className="mb-1 font-medium">{t('data.bootstrap.history')}</div>
                    <ul className="space-y-0.5 text-xs">
                      {runs.slice(0, 3).map(r => (
                        <RunLine key={r.id} run={r} t={t} />
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {launched && (
              <div className="text-sm">{t('data.bootstrap.launched')}</div>
            )}

            {error && <div className="text-sm text-destructive">{error}</div>}

            <DialogFooter>
              <Button variant="outline" onClick={close}>
                {launched ? t('common.close') : t('common.cancel')}
              </Button>
              {!launched && (
                <Button onClick={onLaunch} disabled={launching || !pre?.onboarded}>
                  {launching && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  {t('data.bootstrap.launch')}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
